const LegalShell = ({ title, updated, children, testId }) => (
  <main className="bg-ivory pt-[68px]" data-testid={testId}>
    <div className="mx-auto max-w-[760px] px-5 py-20 sm:px-8 lg:py-24">
      <h1 className="font-serif text-4xl font-medium tracking-tight text-plum sm:text-5xl">
        {title}
      </h1>
      <p className="mt-3 text-xs font-semibold uppercase tracking-[0.18em] text-plumcharcoal/45">
        Last updated: {updated}
      </p>
      <div className="mt-8 space-y-8">{children}</div>
    </div>
  </main>
);

const Section = ({ title, children }) => (
  <section>
    <h2 className="font-serif text-2xl font-semibold tracking-tight text-plum">{title}</h2>
    <div className="mt-3 space-y-3 text-base leading-relaxed text-plumcharcoal/75">
      {children}
    </div>
  </section>
);

const P = ({ children }) => <p>{children}</p>;

const List = ({ items }) => (
  <ul className="list-disc space-y-2 pl-5">
    {items.map((item, i) => (
      <li key={i}>{item}</li>
    ))}
  </ul>
);

const CONTACT = "support@bloomparentify.com";
const UPDATED = "August 2026";

export function Privacy() {
  return (
    <LegalShell title="Privacy Policy" updated={UPDATED} testId="privacy-page">
      <Section title="Overview">
        <P>
          BloomParentify ("we", "us", "our") sells digital products for working
          mothers. This policy explains what information we collect, how we use
          it and the choices you have. We collect only what is needed to run
          this website, deliver your purchases and communicate with you.
        </P>
      </Section>
      <Section title="Information we collect">
        <List
          items={[
            "First name (optional) and email address when you request the free Clear Head Kit or contact us through a website form.",
            "Your email address and order details when you purchase a product.",
            "Basic technical information your browser provides (such as device and browser type) for analytics and site performance.",
          ]}
        />
      </Section>
      <Section title="How we use your information">
        <List
          items={[
            "To deliver the digital products and free resources you request.",
            "To send emails you have asked for, including the Clear Head Kit and related BloomParentify emails. You can unsubscribe at any time using the link in any email.",
            "To process and support your purchase, including resolving payment or delivery issues.",
            "To understand how the website is used so we can improve it.",
          ]}
        />
        <P>We do not sell your personal information.</P>
      </Section>
      <Section title="Service providers">
        <P>
          We use a small number of trusted third-party providers to operate:
        </P>
        <List
          items={[
            "Brevo — stores subscriber details and sends our emails (including the Clear Head Kit).",
            "Razorpay — processes payments. Your payment details are handled by Razorpay and are never stored on our servers.",
            "Website hosting and analytics providers — keep the site available and help us understand aggregate usage.",
          ]}
        />
        <P>
          These providers process information only as needed to provide their
          services to us and under their own privacy terms.
        </P>
      </Section>
      <Section title="Cookies and analytics">
        <P>
          This website may use cookies or similar technologies for essential
          functionality and to understand aggregate site usage. You can control
          cookies through your browser settings. Marketing emails are only sent
          after you submit your email address, and every email includes an
          unsubscribe link.
        </P>
      </Section>
      <Section title="Data security and retention">
        <P>
          We take reasonable measures to protect the information we hold and
          keep it only as long as needed for the purposes above. No method of
          transmission or storage is perfectly secure, but we work with
          reputable providers and limit what we collect.
        </P>
      </Section>
      <Section title="Your choices and rights">
        <P>
          You can unsubscribe from emails at any time, and you can ask us to
          access, correct or delete your personal information by contacting us.
          Depending on where you live, you may have additional rights under
          applicable privacy law, which we respect.
        </P>
      </Section>
      <Section title="Contact">
        <P>
          Questions about this policy or your information:{" "}
          <a href={`mailto:${CONTACT}`} className="font-semibold text-rose underline underline-offset-2">
            {CONTACT}
          </a>
          .
        </P>
      </Section>
    </LegalShell>
  );
}

export function Terms() {
  return (
    <LegalShell title="Terms & Conditions" updated={UPDATED} testId="terms-page">
      <Section title="The product">
        <P>
          The BloomParentify Working Mom System is a bundle of nine digital
          resources (including the Balanced Working Mom Planner and the AI
          Side Hustle Guide). All products are digital. Nothing physical is
          shipped.
        </P>
      </Section>
      <Section title="Purchase and access">
        <P>
          Prices are shown in USD at the point of purchase. After successful
          payment you receive digital access to the product. You are
          responsible for providing a correct email address and for keeping
          your access details safe.
        </P>
      </Section>
      <Section title="Permitted use">
        <P>
          Your purchase grants you a non-exclusive, non-transferable licence
          for personal use. You may not copy, redistribute, resell, share
          publicly or claim the materials as your own. All content remains the
          intellectual property of BloomParentify.
        </P>
      </Section>
      <Section title="Payments">
        <P>
          Payments are processed by Razorpay. By purchasing you agree to
          Razorpay's applicable terms. You authorise us to charge the price
          shown at checkout. See our Refund & Cancellation Policy for how
          refunds are handled.
        </P>
      </Section>
      <Section title="No guaranteed outcomes">
        <P>
          Our products are practical tools and educational resources. They do
          not provide medical, psychological, therapeutic, financial, career
          or legal advice, and we do not guarantee any specific personal,
          productivity or income outcome — including from the AI Side Hustle
          Guide. Results depend on your own circumstances and effort.
        </P>
      </Section>
      <Section title="Availability, changes and support">
        <P>
          We may update, improve or retire products over time. Purchased
          access to a product is not affected by later changes to the public
          offer. For help with a purchase or access issue, contact{" "}
          <a href={`mailto:${CONTACT}`} className="font-semibold text-rose underline underline-offset-2">
            {CONTACT}
          </a>
          .
        </P>
      </Section>
      <Section title="Limitations">
        <P>
          To the extent permitted by applicable law, the website and products
          are provided "as is" and our liability is limited to the amount you
          paid for the product. Nothing in these terms excludes rights or
          guarantees that cannot legally be excluded in your jurisdiction.
        </P>
      </Section>
      <Section title="General">
        <P>
          These terms are governed by the laws applicable in the seller's
          jurisdiction, without prejudice to any mandatory consumer rights you
          have where you live. If any part of these terms is found
          unenforceable, the rest remains in effect.
        </P>
      </Section>
    </LegalShell>
  );
}

export function Refunds() {
  return (
    <LegalShell title="Refund & Cancellation Policy" updated={UPDATED} testId="refunds-page">
      <Section title="Digital products">
        <P>
          BloomParentify products are digital and access is delivered
          immediately after purchase. Because digital content cannot be
          "returned", purchases are generally non-refundable once access,
          download or delivery has been provided.
        </P>
        <P>
          This policy does not exclude or limit any statutory rights you may
          have under applicable consumer law that cannot lawfully be excluded.
        </P>
      </Section>
      <Section title="When we will make it right">
        <P>Contact us and we will resolve or refund, as appropriate, if:</P>
        <List
          items={[
            "You were charged in error or charged more than once for the same purchase (duplicate charge).",
            "Your payment succeeded but you did not receive access to the product.",
            "A technical problem on our side prevents you from accessing what you bought.",
          ]}
        />
      </Section>
      <Section title="Cancellations">
        <P>
          Purchases are one-time, not subscriptions, so there is nothing
          recurring to cancel. You can unsubscribe from our emails at any time
          using the link in any email — this does not affect access to
          products you have purchased.
        </P>
      </Section>
      <Section title="How to request help">
        <P>
          Email{" "}
          <a href={`mailto:${CONTACT}`} className="font-semibold text-rose underline underline-offset-2">
            {CONTACT}
          </a>{" "}
          with the email address used at purchase and a short description of
          the issue. We review every request and aim to respond within 2
          business days. Approved refunds are issued to the original payment
          method through Razorpay.
        </P>
      </Section>
    </LegalShell>
  );
}

export function Shipping() {
  return (
    <LegalShell title="Shipping & Digital Delivery Policy" updated={UPDATED} testId="shipping-page">
      <Section title="No physical shipping">
        <P>
          BloomParentify sells digital products only. No physical product is
          shipped, and no shipping address or shipping charges apply.
        </P>
      </Section>
      <Section title="How delivery works">
        <P>
          After your payment is completed successfully through our Razorpay
          checkout, access to your digital product is provided electronically —
          typically via a download/access link shown after checkout and/or sent
          to the email address you used at purchase.
        </P>
      </Section>
      <Section title="If you don't receive access">
        <List
          items={[
            "Check the inbox (and spam/junk and Promotions folders) of the email address used at checkout.",
            "Wait a few minutes — email delivery can occasionally be delayed.",
            "Still nothing? Email us at the address below with your purchase email and approximate time of purchase, and we will restore your access.",
          ]}
        />
        <P>
          Delivery help:{" "}
          <a href={`mailto:${CONTACT}`} className="font-semibold text-rose underline underline-offset-2">
            {CONTACT}
          </a>
          .
        </P>
      </Section>
    </LegalShell>
  );
}
